/**
 * Batch Purchase Hook
 *
 * Handles multi-item purchases from OBJKT marketplace using Taquito batch transactions.
 * Implements smart retry logic for partial failures.
 */

import { useState, useCallback } from 'react';
import { MichelsonMap } from '@taquito/taquito';
import { useWallet } from '@/hooks/useWallet';
import { confirmCheckoutIntent } from '@/lib/objkt-api';
import type { CheckoutItem } from '@/lib/types/cart';
import { MARKETPLACE_FEE_WALLET, calculateMarketplaceFee } from '@/lib/config';

// Known marketplace contracts and their purchase entrypoints
const OBJKT_MARKETPLACE_V2 = 'KT1WvzYHCNBvDSdwafTHv7nJ1dWmZ8GCYuuC';
// V2: fulfill_ask(ask_id: nat, proxy?: address)

// V3/Newer OBJKT contracts like KT1SwbTqhSKF6Pdokiu1K4Fpi17ahPPzmt1X
// fulfill_ask(ask_id: nat, amount: nat, proxy_for?: address, condition_extra?: bytes, referrers: map)

// fxhash marketplace (used on Ghostnet) - uses listing_accept instead of fulfill_ask
// listing_accept(listing_id: nat)
const FXHASH_MARKETPLACES = [
  'KT1HFYtf4vNCr4xRDZxLKc5asUdCsPUTTW9R', // Ghostnet fxhash
];

type PurchaseStatus = 'idle' | 'signing' | 'confirming' | 'success' | 'partial_success' | 'error';

interface FailedItem {
  listingId: string;
  tokenName?: string;
  reason: string;
}

interface PurchaseResult {
  successfulItems: CheckoutItem[];
  failedItems: FailedItem[];
  operationHash?: string;
  totalSpent?: string;
}

interface UseBatchPurchaseReturn {
  executeBatch: (items: CheckoutItem[], intentId: string) => Promise<void>;
  status: PurchaseStatus;
  error: string | null;
  result: PurchaseResult | null;
  reset: () => void;
}

export function useBatchPurchase(): UseBatchPurchaseReturn {
  const { tezos, address } = useWallet();
  const [status, setStatus] = useState<PurchaseStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PurchaseResult | null>(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setResult(null);
  }, []);

  const executeBatch = useCallback(
    async (items: CheckoutItem[], intentId: string) => {
      if (!tezos || !address) {
        setError('Wallet not connected');
        setStatus('error');
        return;
      }

      if (items.length === 0) {
        setError('No items to purchase');
        setStatus('error');
        return;
      }

      setStatus('signing');
      setError(null);
      setResult(null);

      try {
        // Re-validate items before signing
        console.log('[BatchPurchase] Re-validating', items.length, 'items...');
        console.log('[BatchPurchase] Intent ID:', intentId);
        console.log('[BatchPurchase] Buyer address:', address);

        const validation = await confirmCheckoutIntent(intentId, address);
        console.log('[BatchPurchase] Validation result:', validation);

        if (!validation.valid || validation.validItems.length === 0) {
          setError('All items became unavailable');
          setStatus('error');
          setResult({
            successfulItems: [],
            failedItems: validation.invalidItems.map((item) => ({
              listingId: item.listingId,
              tokenName: item.tokenName,
              reason: item.reason,
            })),
          });
          return;
        }

        // Use only the still-valid items
        const itemsToPurchase = validation.validItems;
        console.log('[BatchPurchase] Purchasing', itemsToPurchase.length, 'valid items');

        // Build batch transaction using toTransferParams for proper amount handling
        // Each fulfill_ask call needs the listing ID (ask_id) and the exact price as amount
        // Items may be from different marketplace contracts, so we cache loaded contracts

        // Calculate total price for marketplace fee
        let totalPriceMutez = BigInt(0);

        // Use chained withContractCall for contract calls (proper method)
        const batch = tezos.wallet.batch();

        // Cache for loaded marketplace contracts (items may be from different contracts)
        const contractCache = new Map<string, Awaited<ReturnType<typeof tezos.wallet.at>>>();

        console.log(`[BatchPurchase] Building batch for ${itemsToPurchase.length} items:`);
        for (let i = 0; i < itemsToPurchase.length; i++) {
          const item = itemsToPurchase[i];
          // Use bigmapKey as the on-chain ask_id (NOT the OBJKT database id)
          const askId = item.bigmapKey;
          const amount = parseInt(item.priceMutez, 10);
          totalPriceMutez += BigInt(item.priceMutez);

          console.log(`[BatchPurchase] Item ${i + 1}/${itemsToPurchase.length}:`, {
            listingId: item.listingId,
            bigmapKey: askId,
            amount,
            marketplaceContract: item.marketplaceContract,
            tokenName: item.tokenName,
          });

          // Get the correct marketplace contract for this item
          const marketplaceAddr = item.marketplaceContract;
          let marketplaceContract = contractCache.get(marketplaceAddr);
          if (!marketplaceContract) {
            console.log(`[BatchPurchase] Loading contract ${marketplaceAddr}`);
            marketplaceContract = await tezos.wallet.at(marketplaceAddr);
            contractCache.set(marketplaceAddr, marketplaceContract);
          }

          console.log(`[BatchPurchase] Adding purchase call for listing ${askId} on ${marketplaceAddr} with ${amount} mutez`);

          // Different marketplace contracts have different purchase entrypoints
          let contractCall;
          if (FXHASH_MARKETPLACES.includes(marketplaceAddr)) {
            // fxhash marketplace - uses listing_accept(listing_id)
            console.log(`[BatchPurchase] Using fxhash listing_accept for ${marketplaceAddr}`);
            contractCall = marketplaceContract.methods.listing_accept(askId);
          } else if (marketplaceAddr === OBJKT_MARKETPLACE_V2) {
            // OBJKT V2 marketplace - fulfill_ask(ask_id, proxy?)
            console.log(`[BatchPurchase] Using OBJKT V2 fulfill_ask for ${marketplaceAddr}`);
            contractCall = marketplaceContract.methods.fulfill_ask(askId, null);
          } else {
            // OBJKT V3/newer marketplaces - fulfill_ask with full signature
            // need amount (token quantity, usually 1 for NFTs)
            // and referrers map to receive referral fees from OBJKT
            // The map is: address -> share (in basis points, 10000 = 100% of referral allocation)
            console.log(`[BatchPurchase] Using OBJKT V3 fulfill_ask for ${marketplaceAddr}`);
            const referrers = new MichelsonMap<string, number>();
            referrers.set(MARKETPLACE_FEE_WALLET, 10000); // 100% of referral fee to our wallet

            contractCall = marketplaceContract.methods.fulfill_ask(
              askId,      // ask_id
              1,          // amount (quantity to purchase, 1 for NFTs)
              null,       // proxy_for (optional)
              null,       // condition_extra (optional)
              referrers   // referrers map - our wallet gets the referral fee
            );
          }

          console.log(`[BatchPurchase] Adding operation ${i + 1}:`, {
            contract: marketplaceAddr,
            askId,
            amount,
          });

          // Use withContractCall which properly handles contract method calls
          batch.withContractCall(contractCall, { amount, mutez: true });
        }

        console.log(`[BatchPurchase] Total items added to batch: ${itemsToPurchase.length}`);
        console.log(`[BatchPurchase] Total price: ${totalPriceMutez} mutez`);

        // Add marketplace fee transfer (1% of total)
        const marketplaceFee = calculateMarketplaceFee(totalPriceMutez);
        if (marketplaceFee > BigInt(0)) {
          console.log(`[BatchPurchase] Adding marketplace fee: ${marketplaceFee} mutez to ${MARKETPLACE_FEE_WALLET}`);
          batch.withTransfer({
            to: MARKETPLACE_FEE_WALLET,
            amount: Number(marketplaceFee),
            mutez: true,
          });
        }

        // Send the batch transaction
        console.log('[BatchPurchase] Sending batch transaction...');
        const operation = await batch.send();
        console.log('[BatchPurchase] Operation hash:', operation.opHash);

        setStatus('confirming');

        // Wait for confirmation
        console.log('[BatchPurchase] Waiting for confirmation...');
        await operation.confirmation(1);
        console.log('[BatchPurchase] Confirmed!');

        // All items succeeded (Tezos transactions are atomic)
        setResult({
          successfulItems: itemsToPurchase,
          failedItems: validation.invalidItems.map((item) => ({
            listingId: item.listingId,
            tokenName: item.tokenName,
            reason: item.reason,
          })),
          operationHash: operation.opHash,
          totalSpent: validation.totals.itemsPriceMutez,
        });

        // Determine final status
        if (validation.invalidItems.length > 0) {
          setStatus('partial_success');
        } else {
          setStatus('success');
        }
      } catch (err: any) {
        console.error('[BatchPurchase] Error:', err);
        console.error('[BatchPurchase] Error JSON:', JSON.stringify(err, null, 2));

        // Check if user rejected
        if (err?.message?.includes('Aborted') || err?.name === 'AbortedBeaconError') {
          setError('Transaction cancelled by user');
          setStatus('error');
          return;
        }

        // Check for specific Tezos errors
        const errorMessage = parseTransactionError(err);
        setError(errorMessage);
        setStatus('error');

        // Try to identify which item(s) failed if possible
        // Note: In atomic batch, all fail together, but we can show the error
        setResult({
          successfulItems: [],
          failedItems: items.map((item) => ({
            listingId: item.listingId,
            tokenName: item.tokenName || undefined,
            reason: errorMessage,
          })),
        });
      }
    },
    [tezos, address]
  );

  return {
    executeBatch,
    status,
    error,
    result,
    reset,
  };
}

/**
 * Extract a readable error message from various error formats
 */
function extractErrorMessage(err: any): string {
  // If it's a string, return it directly
  if (typeof err === 'string') {
    return err;
  }

  // Beacon SDK wallet error format: { errorType, errorData: [{ id, kind }] }
  if (err?.errorType && err?.errorData && Array.isArray(err.errorData)) {
    const errorDetails = err.errorData
      .map((e: any) => e?.id || e?.message || JSON.stringify(e))
      .filter(Boolean)
      .join('; ');

    if (errorDetails) {
      return `${err.errorType}: ${errorDetails}`;
    }
    return err.errorType;
  }

  // Check for Beacon error format (errorType without errorData)
  if (err?.errorType && typeof err.errorType === 'string') {
    return err.errorType;
  }

  // Try common error message properties
  if (err?.message && typeof err.message === 'string') {
    return err.message;
  }

  // Beacon SDK errors often have a description
  if (err?.description && typeof err.description === 'string') {
    return err.description;
  }

  // Taquito errors may have nested errors
  if (err?.errors && Array.isArray(err.errors) && err.errors.length > 0) {
    const firstError = err.errors[0];
    if (firstError?.message) return firstError.message;
    if (firstError?.id) return firstError.id;
    if (typeof firstError === 'string') return firstError;
  }

  // Check for nested error object
  if (err?.error) {
    return extractErrorMessage(err.error);
  }

  // Try to stringify the object if nothing else works
  try {
    const stringified = JSON.stringify(err, null, 2);
    if (stringified !== '{}' && stringified !== 'null') {
      // Truncate long JSON
      return stringified.length > 300 ? stringified.substring(0, 300) + '...' : stringified;
    }
  } catch {
    // Ignore stringify errors
  }

  // Last resort - use toString
  const str = err?.toString?.();
  if (str && str !== '[object Object]') {
    return str;
  }

  return 'Unknown error occurred';
}

/**
 * Parse Tezos transaction errors into user-friendly messages
 */
function parseTransactionError(err: any): string {
  const message = extractErrorMessage(err);

  // Log the full error for debugging
  console.log('[BatchPurchase] Parsed error message:', message);

  // Common OBJKT/Tezos errors
  if (message.includes('AMOUNT_MISMATCH')) {
    return 'Price changed. Please refresh and try again.';
  }
  if (message.includes('ASK_NOT_ACTIVE') || message.includes('ask not found')) {
    return 'One or more items are no longer available.';
  }
  if (message.includes('INSUFFICIENT_BALANCE') || message.includes('balance too low')) {
    return 'Insufficient balance for this purchase.';
  }
  if (message.includes('gas_exhausted') || message.includes('storage_exhausted')) {
    return 'Transaction too complex. Try purchasing fewer items.';
  }
  if (message.includes('counter_in_the_future') || message.includes('counter_in_the_past')) {
    return 'Transaction conflict. Please wait a moment and try again.';
  }
  if (message.includes('branch refused') || message.includes('unknown branch')) {
    return 'Network issue. Please try again.';
  }

  // Return original message if no pattern matched
  return message.length > 200 ? message.substring(0, 200) + '...' : message;
}
